import { LoggerService } from '@core/services/logger.service';

/**
 * Default ceiling for a single blocking APP_INITIALIZER's network work.
 *
 * Generous enough for a slow-but-alive cold load (refresh + effective-permissions
 * + tenant context over a slow link), short enough that a wedged / never-responding
 * bootstrap call cannot trap the user on the index.html "INITIALIZING" splash.
 */
export const BOOTSTRAP_INITIALIZER_TIMEOUT_MS = 8000;

/**
 * Bound a blocking APP_INITIALIZER so it can NEVER wedge application bootstrap.
 *
 * The auth + tenant cold-load initializers each `await` network calls
 * (`/auth/refresh`, `/users/{id}/effective-permissions`, `/tenants`) that have no
 * client-side timeout. If one never responds — a concurrent-refresh
 * cookie-rotation race, or a backend stall — the awaited promise stays pending
 * forever, the `provideAppInitializer` never resolves, Angular bootstrap never
 * completes, and the splash is shown indefinitely (incident 2026-07-01:
 * intermittent hang on authenticated reload).
 *
 * This races the real work against a timer that **resolves** (never rejects — a
 * rejected initializer would trip main.ts's bootstrap `.catch` and show the red
 * "Application Failed to Start" box). On timeout we let bootstrap proceed; the
 * route guards (`whenAuthReady`) and the auth interceptor's refresh-then-retry
 * then drive the normal logged-in / redirect-to-login flow instead of an
 * infinite spinner. Late settlement of the original work is swallowed so it
 * cannot raise an unhandled rejection after the race has already resolved.
 *
 * @param work   the initializer's real promise (must be fail-safe — it should
 *               own its own logout/redirect on failure; this wrapper only bounds
 *               how long bootstrap waits for it).
 * @param label  human-readable name for diagnostics.
 * @param logger app logger (Sentry-wired) for the timeout/rejection breadcrumb.
 * @param ms     timeout in milliseconds; defaults to
 *               {@link BOOTSTRAP_INITIALIZER_TIMEOUT_MS}.
 */
export function withBootstrapTimeout(
  work: Promise<unknown>,
  label: string,
  logger: LoggerService,
  ms: number = BOOTSTRAP_INITIALIZER_TIMEOUT_MS,
): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout>;

    const finish = (): void => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      resolve();
    };

    timer = setTimeout(() => {
      if (settled) {
        return;
      }
      logger.warn(
        `[bootstrap] "${label}" exceeded ${ms}ms; proceeding so startup is never wedged`,
        { module: 'BootstrapTimeout', label, ms },
      );
      finish();
    }, ms);

    work.then(finish, (err: unknown) => {
      // The blocking initializers are fail-safe and should not reject; if one
      // does, treat it as completion (the app must still boot) and record it.
      if (!settled) {
        logger.warn(
          `[bootstrap] "${label}" rejected; proceeding so startup is never wedged`,
          { module: 'BootstrapTimeout', label, err },
        );
      }
      finish();
    });
  });
}
